import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ShieldAlert, Loader2, Check, X } from 'lucide-react';

const SEVERITY_STYLE = {
  minor: 'bg-amber-100 text-amber-800 border-amber-200',
  major: 'bg-orange-100 text-orange-800 border-orange-200',
  severe: 'bg-red-100 text-red-800 border-red-200',
};
const CATEGORY_LABEL = {
  no_show: 'No-show', left_early: 'Left early', late: 'Late', unprofessional: 'Unprofessional',
  dress_code: 'Dress code', solicitation: 'Solicitation', off_platform: 'Off-platform',
  falsified_records: 'Falsified records', incomplete_media: 'Incomplete media', safety: 'Safety', other: 'Other',
};

/** CoCViolationsPanel — talent reliability record: Code of Conduct breach flags. */
export const CoCViolationsPanel = ({ talentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/talent/${talentId}/coc-violations`);
      setData(r.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [talentId]);

  useEffect(() => { if (talentId) load(); }, [talentId, load]);

  const update = async (id, status) => {
    const note = status === 'dismissed' ? (window.prompt('Reason for dismissing (optional):') || '') : (window.prompt('Resolution note (optional):') || '');
    try {
      await api.patch(`/admin/coc-violations/${id}`, { status, resolution_note: note });
      toast.success(status === 'resolved' ? 'Marked resolved' : 'Dismissed');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const items = data?.violations || [];

  return (
    <Card data-testid="coc-violations-panel">
      <CardHeader className="py-3 px-5 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <ShieldAlert className="w-4 h-4 text-red-500" /> Code of Conduct
        </div>
        {data && (
          <Badge className={`text-[11px] ${data.open_count > 0 ? 'bg-red-100 text-red-800 border-red-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200'}`} data-testid="coc-open-count">
            {data.open_count} open · {data.total_count} total
          </Badge>
        )}
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400 py-2" data-testid="coc-violations-empty">No Code of Conduct breaches flagged. Clean record.</p>
        ) : (
          <div className="space-y-2">
            {items.map((v) => (
              <div key={v.id} className="border border-slate-200 rounded-lg p-3" data-testid={`coc-violation-${v.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800 text-sm">{CATEGORY_LABEL[v.category] || v.category}</span>
                  <Badge className={`text-[10px] border ${SEVERITY_STYLE[v.severity] || ''}`}>{v.severity}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${v.status === 'open' ? 'text-red-700 border-red-300' : 'text-slate-500'}`}>{v.status}</Badge>
                  <span className="text-[11px] text-slate-400 ml-auto">{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
                </div>
                {v.event_title && <p className="text-xs text-slate-500 mt-1">{v.event_title}{v.client_name ? ` · ${v.client_name}` : ''}{v.shift_date ? ` · ${v.shift_date}` : ''}</p>}
                {v.client_complaint && <p className="text-sm text-slate-700 mt-1"><span className="text-slate-400 text-xs">Complaint:</span> {v.client_complaint}</p>}
                {v.description && <p className="text-sm text-slate-600 mt-1">{v.description}</p>}
                <p className="text-[11px] text-slate-400 mt-1">Flagged by {v.flagged_by_name || 'admin'}{v.coc_version_label ? ` · CoC ${v.coc_version_label}` : ''}</p>
                {v.status !== 'open' && v.resolution_note && <p className="text-[11px] text-slate-500 mt-1 italic">"{v.resolution_note}" — {v.resolved_by_name}</p>}
                {v.status === 'open' && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => update(v.id, 'resolved')} data-testid={`coc-resolve-${v.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Resolve
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => update(v.id, 'dismissed')} data-testid={`coc-dismiss-${v.id}`}>
                      <X className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CoCViolationsPanel;
